import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { crmService } from '../services/crmService';

type TimeRangeType = 'week' | 'month' | 'quarter' | 'year';

type CRMAnalyticsData = {
  leads: {
    total: number;
    qualified: number;
    newThisPeriod: number;
    qualificationRate: number;
    conversionRate: number;
    converted: number;
    bySource: { source: string; count: number; percentage: number }[];
    byIndustry: { industry: string; count: number; percentage: number }[];
  };
  revenue: {
    total: number;
    thisPeriod: number;
    averageDealSize: number;
    bySource: { source: string; amount: number; percentage: number }[];
  };
  contacts: {
    total: number;
    active: number;
    newThisPeriod: number;
    emailsSent: number;
    callsMade: number;
  };
};

const AnalyticsPage: React.FC = () => {
  const theme = useTheme();
  const [analytics, setAnalytics] = useState<CRMAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRangeType>('month');

  // Notifications
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Load analytics
  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const data = await crmService.getAnalytics(timeRange);
      // Transform the API response to match our local interface
      const transformedData: CRMAnalyticsData = {
        leads: {
          total: data.totalLeads || 0,
          qualified: data.qualifiedLeads || 0,
          newThisPeriod: 0,
          qualificationRate: data.qualifiedLeads / (data.totalLeads || 1),
          conversionRate: data.conversionRate || 0,
          converted: 0,
          bySource: data.leadSources?.map(s => ({ source: s.source, count: s.count, percentage: 0 })) || [],
          byIndustry: data.topIndustries?.map(i => ({ industry: i.industry, count: i.count, percentage: 0 })) || [],
        },
        revenue: {
          total: data.revenueGenerated || 0,
          thisPeriod: data.revenue?.total || 0,
          averageDealSize: 0,
          bySource: data.revenue?.bySource || [],
        },
        contacts: {
          total: 0,
          active: 0,
          newThisPeriod: 0,
          emailsSent: 0,
          callsMade: 0,
        },
      };
      setAnalytics(transformedData);
    } catch (error) {
      showSnackbar('Failed to load analytics', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Utility functions
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num);
  };

  const formatPercentage = (num: number) => {
    return `${(num * 100).toFixed(1)}%`;
  };

  // Effects
  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  if (!analytics) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          p: 4,
        }}
      >
        <Alert
          severity="error"
          sx={{
            bgcolor: alpha(theme.palette.error.main, 0.9),
            color: 'common.white',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          Failed to load analytics data
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <Box sx={{ maxWidth: '1400px', mx: 'auto', py: 4, px: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h3" sx={{ color: 'text.primary', fontWeight: 700, mb: 1 }}>
              Unified Analytics Dashboard
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Track performance metrics and insights
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel sx={{ color: 'text.secondary' }}>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={(e) => setTimeRange(e.target.value as TimeRangeType)}
                sx={{
                  color: 'text.primary',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                  '& .MuiSvgIcon-root': { color: 'text.primary' },
                }}
              >
                <MenuItem value="week">Last week</MenuItem>
                <MenuItem value="month">Last month</MenuItem>
                <MenuItem value="quarter">Last quarter</MenuItem>
                <MenuItem value="year">Last year</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadAnalytics}
              disabled={loading}
              sx={{
                color: 'text.primary',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PeopleIcon sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Total Leads
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ mb: 1, color: 'text.primary', fontWeight: 700 }}>
                {formatNumber(analytics.leads.total)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'success.main' }}>
                +{formatNumber(analytics.leads.newThisPeriod)} new
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Qualified Leads
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ mb: 1, color: 'text.primary', fontWeight: 700 }}>
                {formatNumber(analytics.leads.qualified)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'info.main' }}>
                {formatPercentage(analytics.leads.qualificationRate)} qualification rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Conversion Rate
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ mb: 1, color: 'text.primary', fontWeight: 700 }}>
                {formatPercentage(analytics.leads.conversionRate)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'success.main' }}>
                {formatNumber(analytics.leads.converted)} converted
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EmailIcon sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Revenue Generated
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ mb: 1, color: 'text.primary', fontWeight: 700 }}>
                {formatCurrency(analytics.revenue.total)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'success.main' }}>
                +{formatCurrency(analytics.revenue?.thisPeriod || 0)} this period
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Analytics */}
      <Grid container spacing={3}>
        {/* Lead Sources */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                Lead Sources
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 300, bgcolor: 'background.paper' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary' }}>Source</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary' }}>Count</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary' }}>Percentage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.leads?.bySource.map((source: { source: string; count: number; percentage: number }) => (
                      <TableRow key={source.source}>
                        <TableCell sx={{ color: 'text.primary' }}>{source.source}</TableCell>
                        <TableCell align="right" sx={{ color: 'text.primary' }}>{formatNumber(source.count)}</TableCell>
                        <TableCell align="right" sx={{ color: 'text.primary' }}>{formatPercentage(source.percentage)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Industry Breakdown */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                Leads by Industry
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 300, bgcolor: 'background.paper' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary' }}>Industry</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary' }}>Count</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary' }}>Percentage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.leads?.byIndustry.map((industry: { industry: string; count: number; percentage: number }) => (
                      <TableRow key={industry.industry}>
                        <TableCell sx={{ color: 'text.primary' }}>{industry.industry}</TableCell>
                        <TableCell align="right" sx={{ color: 'text.primary' }}>{formatNumber(industry.count)}</TableCell>
                        <TableCell align="right" sx={{ color: 'text.primary' }}>{formatPercentage(industry.percentage)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Contact Activity */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                Contact Activity
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Total Contacts: {formatNumber(analytics.contacts.total)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Active Contacts: {formatNumber(analytics.contacts.active)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  New This Period: {formatNumber(analytics.contacts.newThisPeriod)}
                </Typography>
              </Box>

              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, color: 'text.primary' }}>
                Communication Stats
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon sx={{ color: 'primary.main', fontSize: 16 }} />
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {formatNumber(analytics.contacts.emailsSent)} emails sent
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon sx={{ color: 'secondary.main', fontSize: 16 }} />
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {formatNumber(analytics.contacts.callsMade)} calls made
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue Breakdown */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                Revenue Breakdown
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Total Revenue: {formatCurrency(analytics.revenue.total)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  This Period: {formatCurrency(analytics.revenue.thisPeriod)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Average Deal Size: {formatCurrency(analytics.revenue.averageDealSize)}
                </Typography>
              </Box>

              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, color: 'text.primary' }}>
                Revenue by Source
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 200, bgcolor: 'background.paper' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary' }}>Source</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary' }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.revenue?.bySource.map((source: { source: string; amount: number; percentage: number }) => (
                      <TableRow key={source.source}>
                        <TableCell sx={{ color: 'text.primary' }}>{source.source}</TableCell>
                        <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>
                          {formatCurrency(source.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            bgcolor: snackbar.severity === 'error' ? alpha(theme.palette.error.main, 0.9) :
                    snackbar.severity === 'success' ? alpha(theme.palette.success.main, 0.9) :
                    alpha(theme.palette.info.main, 0.9),
            color: 'common.white',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </Box>
  );
};

export default AnalyticsPage;